import { useCallback, useRef, useState } from 'react';
import { CategoryPublication, getCategoriesPublications } from '../../api/category';
import { getCategoryFlowErrorMessage } from '../../api/problemJson';
import { Option } from '../../types/option';

const PUBLICATIONS_LOAD_ERROR_MESSAGE =
    'Impossible de charger les publications de cette catégorie.';

export function useCategoryPublicationsMap() {
    const [publicationsByCategory, setPublicationsByCategory] = useState<
        Map<number, CategoryPublication[]>
    >(new Map());
    const [loadingCategoryIds, setLoadingCategoryIds] = useState<ReadonlySet<number>>(new Set());
    const [publicationsLoadErrors, setPublicationsLoadErrors] = useState<
        ReadonlyMap<number, string>
    >(new Map());
    const mapRef = useRef(publicationsByCategory);
    mapRef.current = publicationsByCategory;

    const startLoading = useCallback((categoryId: number) => {
        setLoadingCategoryIds(prev => new Set(prev).add(categoryId));
    }, []);

    const stopLoading = useCallback((categoryId: number) => {
        setLoadingCategoryIds(prev => {
            const next = new Set(prev);
            next.delete(categoryId);
            return next;
        });
    }, []);

    const loadPublicationsForCategory = useCallback(
        async (categoryId: number) => {
            if (mapRef.current.has(categoryId)) {
                return;
            }
            startLoading(categoryId);
            try {
                const publications = await getCategoriesPublications(categoryId);
                setPublicationsByCategory(prev => new Map(prev).set(categoryId, publications));
                setPublicationsLoadErrors(prev => {
                    const next = new Map(prev);
                    next.delete(categoryId);
                    return next;
                });
            } catch (error: unknown) {
                setPublicationsLoadErrors(prev =>
                    new Map(prev).set(
                        categoryId,
                        getCategoryFlowErrorMessage(error, PUBLICATIONS_LOAD_ERROR_MESSAGE),
                    ),
                );
            } finally {
                stopLoading(categoryId);
            }
        },
        [startLoading, stopLoading],
    );

    const refreshPublicationsForCategory = useCallback(
        async (categoryId: number) => {
            startLoading(categoryId);
            try {
                const publications = await getCategoriesPublications(categoryId);
                setPublicationsByCategory(prev => new Map(prev).set(categoryId, publications));
                setPublicationsLoadErrors(prev => {
                    const next = new Map(prev);
                    next.delete(categoryId);
                    return next;
                });
            } catch (error: unknown) {
                setPublicationsLoadErrors(prev =>
                    new Map(prev).set(
                        categoryId,
                        getCategoryFlowErrorMessage(error, PUBLICATIONS_LOAD_ERROR_MESSAGE),
                    ),
                );
            } finally {
                stopLoading(categoryId);
            }
        },
        [startLoading, stopLoading],
    );

    const getPublicationsLoadError = useCallback(
        (categoryId: number): Option<string> => {
            const message = publicationsLoadErrors.get(categoryId);

            return message ? Option.some(message) : Option.none();
        },
        [publicationsLoadErrors],
    );

    const isCategoryLoading = useCallback(
        (categoryId: number) => loadingCategoryIds.has(categoryId),
        [loadingCategoryIds],
    );

    return {
        publicationsByCategory,
        loadPublicationsForCategory,
        refreshPublicationsForCategory,
        getPublicationsLoadError,
        loadingCategoryIds,
        isCategoryLoading,
    };
}
